import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import I18n from 'i18n-js';
import {
  IAnalyticRubricCriterion, IAnalyticRubricLevel,
} from '../../useQuestionRubric';

type CriterionCellProps = {
  data: IAnalyticRubricCriterion;
  locale?: Lowercase<Locale>;
  onCriterionNameChange: (criterionIndex: number, newName: string) => void;
  onDeleteCriterion: (criterionIndex: number) => void;
  rowIndex: number;
  totalCriteria: number;
};

type LevelCellProps = {
  criterionData: IAnalyticRubricCriterion;
  locale?: Lowercase<Locale>;
  rowIndex: number;
  score: number;
  onLevelDescriptionChange: (criterionIndex: number, score: number, newDescription: string) => void;
};

type AnalyticRubricColumnSizes = {
  criterion?: string;
  level?: string;
};

type CreateAnalyticRubricColumnsArgs = {
  CriterionCell: React.ComponentType<CriterionCellProps>;
  LevelCell: React.ComponentType<LevelCellProps>;
  cellProps?: {
    onCriterionNameChange?: (criterionIndex: number, newName: string) => void;
    onDeleteCriterion?: (criterionIndex: number) => void;
    onLevelDescriptionChange?: (criterionIndex: number, score: number, newDescription: string) => void;
    totalCriteria?: number;
  };
  locale?: Lowercase<Locale>;
  scoreLevels?: number[];
  columnSizes?: AnalyticRubricColumnSizes;
};

export const TEXT_AREA_CLASSES = 'tw-w-full tw-border-none tw-resize-none tw-text-tci-black tw-placeholder-tci-black';
export const TEXT_AREA_CSS = { fontFamily: 'Lato', fontSize: '14px', height: '4.5rem' };

const columnHelper = createColumnHelper<IAnalyticRubricCriterion>();

type DefaultLevelArgs = {
  levelId: number;
  score: number;
};

export const DEFAULT_LEVEL = ({ levelId, score }: DefaultLevelArgs):
IAnalyticRubricLevel => ({
  description_en: '',
  description_es: '',
  description_id: null,
  level_id: levelId,
  score,
});

export const createAnalyticRubricColumns = ({
  CriterionCell,
  LevelCell,
  cellProps = {},
  locale = 'en',
  scoreLevels = [4, 3, 2, 1],
  columnSizes = {
    criterion: '200px',
    level: 'auto',
  },
}: CreateAnalyticRubricColumnsArgs) => {
  const { onCriterionNameChange, onDeleteCriterion, onLevelDescriptionChange, totalCriteria } = cellProps;
  const criterionSize = columnSizes.criterion;
  const levelSize = columnSizes.level;

  const criterionColumn = columnHelper.display({
    cell: info => (
      <CriterionCell
        data={info.row.original}
        locale={locale}
        rowIndex={info.row.index}
        totalCriteria={totalCriteria}
        onCriterionNameChange={onCriterionNameChange}
        onDeleteCriterion={onDeleteCriterion}
      />
    ),
    header: I18n.t('criteria'),
    id: 'criterion',
    meta: { width: criterionSize },
  });

  const levelColumns = scoreLevels.map(score => columnHelper.display({
    cell: info => (
      <LevelCell
        criterionData={info.row.original}
        locale={locale}
        rowIndex={info.row.index}
        score={score}
        onLevelDescriptionChange={onLevelDescriptionChange}
      />
    ),
    header: score.toString(),
    id: `level_${score}`,
    meta: { width: levelSize },
  }));

  return [criterionColumn, ...levelColumns];
};
